/**
 * PassportPaperConfirmModal
 * Full-screen overlay that pops the moment a passport-photo order lands in the
 * retailer's queue (held at status="awaiting_retailer_confirm"). The retailer
 * must physically load 4×6" glossy photo paper and then tap "Glossy Paper
 * Loaded" — that call flips the order to `pending` and wakes the connector's
 * long-poll, so the print fires within ~100 ms.
 *
 * On top of the modal (visual, mandatory), we ALSO fire:
 *   • A browser Notification (mandatory when permission is granted) so the
 *     retailer sees the alert even when the dashboard tab is in the background.
 *   • A soft 2-beep chime via Web Audio API — best-effort only. Machines
 *     without a sound card / with muted output still work perfectly; we never
 *     block the flow on audio failure.
 *
 * Wired into <Layout /> so it appears everywhere on the retailer's app the
 * moment a walk-in passport order arrives — no page refresh required.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2, User, Phone, ImageIcon, Bell } from "lucide-react";

const POLL_INTERVAL_MS = 4000;

/* ---------- Audio: best-effort 2-beep chime via Web Audio API --------------
 * Uses OscillatorNode so we don't ship an .mp3 asset. If the browser doesn't
 * expose AudioContext (rare), or the machine has no output device, or the
 * user's autoplay policy has us suspended, we silently no-op. The modal +
 * browser notification remain the mandatory alert channels. */
let _sharedAudioCtx = null;
function _getAudioCtx() {
  if (_sharedAudioCtx) return _sharedAudioCtx;
  const Ctor = (typeof window !== "undefined") && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) return null;
  try { _sharedAudioCtx = new Ctor(); } catch { return null; }
  return _sharedAudioCtx;
}
function playChime() {
  const ac = _getAudioCtx();
  if (!ac) return; // No audio hardware / API — silent, non-fatal.
  try {
    // Autoplay policy may keep the context suspended until the user has
    // interacted. resume() is best-effort; if it fails we still don't throw.
    if (ac.state === "suspended") { ac.resume().catch(() => {}); }
    const now = ac.currentTime;
    const beep = (freq, offset, dur) => {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.16, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + dur);
      osc.connect(gain).connect(ac.destination);
      osc.start(now + offset);
      osc.stop(now + offset + dur + 0.02);
    };
    beep(880, 0, 0.18);      // A5
    beep(1174.66, 0.22, 0.24); // D6 — pleasant "ding-dong" pair
  } catch { /* Any audio error is non-fatal — modal is still up. */ }
}

/* ---------- Notification: mandatory when permission is granted ------------- */
function _hasNotifSupport() {
  return typeof window !== "undefined" && "Notification" in window;
}
async function ensureNotifPermission() {
  if (!_hasNotifSupport()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "denied";
  }
}
function fireNotification(order) {
  if (!_hasNotifSupport() || Notification.permission !== "granted") return;
  try {
    const n = new Notification("📸 Passport Photo Order — Load 4×6\" Glossy Paper", {
      body: `${order.customer_name || "Walk-in customer"} · ₹${Number(order.cost || 0).toFixed(2)} · ${order.filename || "passport sheet"}`,
      tag: `maaprint-passport-${order.id}`, // Prevents duplicates for the same order.
      requireInteraction: true,             // Sticky — stays until the retailer dismisses.
      icon: "/favicon.ico",
      badge: "/favicon.ico",
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    /* Some browsers throw on background pages / iframes — ignore. */
  }
}

export default function PassportPaperConfirmModal() {
  const { user } = useAuth();
  const [held, setHeld] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    _hasNotifSupport() ? Notification.permission : "unsupported"
  );
  // IDs we've already alerted for — prevents re-firing chime/notif on every
  // 4s poll tick for the same order.
  const alertedIdsRef = useRef(new Set());

  // Anyone who can physically fulfil an order needs the "load paper" nag.
  // Includes distributors (who often run their own shops) and super_admins
  // (who may operate the platform's flagship / demo shop). The endpoint
  // `/orders/awaiting-confirmation` scopes by user_id for non-admins and
  // returns everything for super_admin — so a super_admin still only nags
  // for orders under THEIR user_id since orders_awaiting_confirmation
  // filters by user_id (see server.py). Customers never see this.
  const eligible = user && (user.role === "retailer"
                            || user.role === "distributor"
                            || user.role === "super_admin");

  // ---- Permission bootstrap ------------------------------------------------
  // Ask on mount. Some browsers require a user gesture — if `default` remains
  // after the initial ask, we retry on the first click anywhere in the app.
  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    const unlock = async () => {
      const ac = _getAudioCtx();
      if (ac && ac.state === "suspended") { try { await ac.resume(); } catch {} }
      if (_hasNotifSupport() && Notification.permission === "default") {
        const p2 = await ensureNotifPermission();
        if (!cancelled) setNotifPerm(p2);
      }
    };
    (async () => {
      const p = await ensureNotifPermission();
      if (!cancelled) setNotifPerm(p);
      document.addEventListener("click", unlock, { once: true });
      document.addEventListener("touchstart", unlock, { once: true });
    })();
    return () => {
      cancelled = true;
      // Explicit removal covers unmount BEFORE the user has clicked; the
      // { once: true } option handles removal after firing.
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
    };
  }, [eligible]);

  const fetchHeld = useCallback(async () => {
    if (!eligible) return;
    try {
      const { data } = await api.get("/orders/awaiting-confirmation");
      const list = Array.isArray(data) ? data : [];
      // Fire alert side-effects only for genuinely NEW orders — orders we
      // haven't seen in a previous poll. Prevents 4s-repeating chimes.
      let sawNew = false;
      for (const o of list) {
        if (!alertedIdsRef.current.has(o.id)) {
          alertedIdsRef.current.add(o.id);
          fireNotification(o);
          sawNew = true;
        }
      }
      // Debounce chime to one beep per poll tick — if two brand-new orders
      // arrive in the same fetch we'd otherwise play them ~0ms apart and
      // they'd overlap into noise.
      if (sawNew) playChime();
      // Prune the ref set to just the still-live orders so if a confirmed
      // order somehow reappears (edge case, e.g. admin re-holds), we alert
      // again.
      const liveIds = new Set(list.map((o) => o.id));
      for (const id of Array.from(alertedIdsRef.current)) {
        if (!liveIds.has(id)) alertedIdsRef.current.delete(id);
      }
      setHeld(list);
    } catch {
      /* Network hiccups are fine — we'll retry on the next tick. */
    }
  }, [eligible]);

  useEffect(() => {
    if (!eligible) return;
    fetchHeld();
    const iv = setInterval(fetchHeld, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [eligible, fetchHeld]);

  const top = held[0];
  const confirm = async () => {
    if (!top) return;
    setConfirming(true);
    try {
      await api.post(`/orders/${top.id}/confirm-paper-loaded`);
      toast.success("Sent to printer — printing now.", { duration: 3000 });
      // Optimistically drop this order from the queue so the next one (if any)
      // takes over immediately without waiting for the next poll tick.
      setHeld((prev) => prev.filter((o) => o.id !== top.id));
      // Refresh to catch any orders that arrived while we were confirming.
      setTimeout(fetchHeld, 400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not confirm — please retry.");
    } finally {
      setConfirming(false);
    }
  };

  if (!eligible || !top) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-background/85 backdrop-blur-md px-4 py-6 animate-in fade-in"
      data-testid="passport-confirm-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-xl rounded-2xl border border-primary/25 bg-card shadow-2xl overflow-hidden">
        {/* Header band with camera icon */}
        <div className="px-8 pt-8 pb-6 bg-gradient-to-b from-primary/[0.07] to-transparent text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-primary/12 text-primary flex items-center justify-center">
            <Camera size={28} />
          </div>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
            Passport Size Photo Order Received
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            Please load <span className="font-semibold text-foreground">4×6 inch Glossy Photo Paper</span>{" "}
            into the printer before printing. This order will not auto-print until you confirm.
          </p>
        </div>

        {/* Order metadata */}
        <div className="px-8 pb-2 space-y-3">
          {/* Notification permission prompt — mandatory alert channel. Only
              rendered when permission is not yet granted; disappears once
              the retailer allows notifications (or if the browser denies). */}
          {notifPerm !== "granted" && notifPerm !== "unsupported" && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5"
                 data-testid="passport-notif-permission-prompt">
              <Bell size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-700 dark:text-amber-500">
                  Turn on browser alerts
                </p>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Get notified even when this tab is in the background — so you never miss a passport order.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-amber-500/40 text-amber-700 hover:bg-amber-500/10"
                onClick={async () => {
                  const p = await ensureNotifPermission();
                  setNotifPerm(p);
                  if (p === "granted") {
                    // Re-alert the current top order so the retailer sees the
                    // notification fire immediately after allowing.
                    if (top) fireNotification(top);
                    toast.success("Alerts enabled — you'll be pinged for every new order.");
                  } else if (p === "denied") {
                    toast.error("Browser blocked alerts. Enable them in the site settings.");
                  }
                }}
                data-testid="passport-notif-enable-btn"
              >
                Allow
              </Button>
            </div>
          )}

          <div className="rounded-xl border border-border bg-secondary/50 divide-y divide-border">
            <MetaRow label="File" value={top.filename} testId="passport-modal-file" />
            <MetaRow
              label="Layout"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <ImageIcon size={12} /> 4 × 2 · 8 photos · 300 DPI
                </span>
              }
            />
            {top.passport_size && (
              <MetaRow label="Photo size" value={humaniseSize(top.passport_size)} />
            )}
            {top.customer_name && (
              <MetaRow
                label="Customer"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <User size={12} /> {top.customer_name}
                    {top.customer_phone && (
                      <>
                        <span className="opacity-30">·</span>
                        <Phone size={12} /> {top.customer_phone}
                      </>
                    )}
                  </span>
                }
              />
            )}
            <MetaRow
              label="Amount received"
              value={
                <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/25 font-semibold">
                  ₹{Number(top.cost || 0).toFixed(2)} PAID
                </Badge>
              }
            />
          </div>

          {held.length > 1 && (
            <p className="text-[11px] text-center text-muted-foreground pt-1"
               data-testid="passport-modal-queue-count">
              {held.length - 1} more passport {held.length - 1 === 1 ? "order is" : "orders are"} waiting
              behind this one.
            </p>
          )}
        </div>

        {/* Confirmation CTA */}
        <div className="p-8 pt-4">
          <Button
            onClick={confirm}
            disabled={confirming}
            className="w-full h-14 text-base font-semibold bg-brand-green hover:bg-brand-green-hover text-white shadow-lg"
            data-testid="passport-modal-confirm-btn"
          >
            {confirming ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" /> Sending to printer…
              </>
            ) : (
              <>
                <CheckCircle2 size={18} className="mr-2" /> 4×6 Glossy Paper Loaded — Start Print
              </>
            )}
          </Button>
          <p className="mt-3 text-[11px] text-center text-muted-foreground leading-relaxed">
            Do NOT use A4 paper. Use only 4×6 inch glossy photo paper so the 8 passport tiles land
            at the correct size and quality.
          </p>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium max-w-[260px] truncate text-right" data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

const SIZE_LABELS = {
  IN_PASSPORT: "India Passport (35×45)",
  PAN_CARD: "PAN Card (25×35)",
  AADHAAR: "Aadhaar (35×45)",
  US_VISA: "US Visa (51×51)",
  SCHENGEN: "Schengen (35×45)",
  UK_PASSPORT: "UK Passport (35×45)",
};
function humaniseSize(key) {
  return SIZE_LABELS[key] || key;
}
